import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Axios from 'axios';

import intlShape from '@/shared/utils/intlShape';
import SimplePanel from '@/shared/components/SimplePanel';
import { ColumnHeader, ScrollableTable } from '@/shared/components/table';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import moment from 'moment';
import api from '@/shared/api';
import config from '@/config';
import { injectStateService, StateService } from '@/shared/services/StateService';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import Checkbox from '@/shared/components/input/Checkbox';
import Button from '@/shared/components/Button';
import handleHttpError from '@/shared/services/HandleHttpError';
import { AddIcon } from '@/shared/components/Icons';
import FormattedListValue, { listItemMsg } from '@/shared/components/FormattedListValue';
import TrainingOverviewFilters, { defaultFilters } from './TrainingOverviewFilters';
import RegistrationModal from '@/shared/components/registration/RegistrationModal';
import EmployeeTrainingModal from './EmployeeTrainingModal';
import AuthService from '@/shared/services/AuthService';
import TrainingEmployeeOverviewRow from './TrainingEmployeeOverviewRow';
import messages from './trainingMessages';
import { trainingRegistrationOptions } from '@/shared/components/registration/registrationHelpers';

const SessionStorageKey = 'TrainingEmployeeOverviewParams';

class TrainingEmployeeOverviewContainer extends Component {
  constructor(props) {
    super(props);
    const { searchParams = {}, intl } = props;
    const { trainingRegistrationFilters: sessionFilters = {}, ...sessionParams } =
      JSON.parse(sessionStorage.getItem(SessionStorageKey)) || {};

    if (sessionFilters.dateFrom) {
      sessionFilters.dateFrom = moment.utc(sessionFilters.dateFrom);
    }
    if (sessionFilters.dateUntil) {
      sessionFilters.dateUntil = moment.utc(sessionFilters.dateUntil);
    }

    this.currentUserId = AuthService.getUserId();

    this.state = {
      items: [],
      currentSearchParams: undefined,
      allSelected: false,
      initialSearchParams: {
        // Set default filters
        sortKey: 'name',
        pageSize: -1,
        trainingRegistrationFilters: {
          ...defaultFilters,
          ...sessionFilters,
          ...searchParams,
        },
        // Override with session storage params
        ...sessionParams,
      },
      showTrainingRegistrationModal: false,
      showEmployeeTrainingModal: false,
      selectedEmployeeTraining: null,
    };
  }

  componentDidMount() {}

  componentWillUnmount() {
    if (
      this.trainingRegistrationCancelToken &&
      !this.trainingRegistrationCancelToken.token.reason
    ) {
      this.trainingRegistrationCancelToken.cancel('Operation canceled due to leaving page.');
    }
  }

  loadItems = searchParams => {
    sessionStorage.setItem(
      SessionStorageKey,
      JSON.stringify({
        desc: searchParams.desc,
        sortKey: searchParams.sortKey,
        trainingFilters: searchParams.trainingRegistrationFilters,
      }),
    );
    const { allSelected } = this.state;
    const { intl } = this.props;
    if (this.trainingCancelToken && !this.trainingCancelToken.token.reason) {
      this.trainingCancelToken.cancel('Operation canceled due to new request.');
    }
    this.trainingCancelToken = Axios.CancelToken.source();

    const { period, ...filtersWithoutPeriod } = searchParams.trainingRegistrationFilters;

    const cleanedFilters = {
      ...filtersWithoutPeriod,
      dateFrom: filtersWithoutPeriod.dateFrom
        ? searchParams.trainingRegistrationFilters.dateFrom.toISOString()
        : undefined,
      dateUntil: filtersWithoutPeriod.dateUntil
        ? searchParams.trainingRegistrationFilters.dateUntil.toISOString()
        : undefined,
      userId: this.currentUserId,
      trainingStatuses: searchParams.trainingRegistrationFilters.trainingStatuses || [],
    };

    const query = new URLSearchParams({
      desc: searchParams.desc,
      sort: searchParams.sortKey,
    }).toString();

    return api.base
      .post(`${config.appUrls.api}/api/TrainingEmployee/List?${query}`, cleanedFilters, {
        cancelToken: this.trainingCancelToken.token,
      })
      .then(({ data }) => {
        const items = data.data.map(e => ({ ...e, selected: !!allSelected }));
        this.setState(state => ({
          ...state,
          items,
          currentSearchParams: searchParams,
        }));

        return items;
      })
      .catch(error => {
        if (!Axios.isCancel(error)) return error;
      });
  };

  onTrainingRegistrationSaved = trainingRegistration => {
    const { currentSearchParams } = this.state;

    this.setState(
      {
        showTrainingRegistrationModal: false,
        selectedEmployeeTraining: null,
      },
      () => {
        if (currentSearchParams) {
          this.loadItems(currentSearchParams);
        }
      },
    );
  };

  filtersChanged = filters => {
    this.setState(state => ({
      initialSearchParams: {
        ...state.initialSearchParams,
        page: 0,
        trainingRegistrationFilters: filters,
      },
    }));
  };

  handleSelect = row => {
    this.setState(({ items }) => ({
      items: items.map(x => (x.id === row.id ? { ...x, selected: !x.selected } : x)),
    }));
  };

  handleSelectAll = () => {
    this.setState(({ items, allSelected }) => ({
      items: items.map(x => ({ ...x, selected: !allSelected })),
      allSelected: !allSelected,
    }));
  };

  openTrainingRegistrationModal = training => {
    const options = { ...trainingRegistrationOptions, trainingReadOnly: true };
    this.setState({
      selectedEmployeeTraining: {
        trainingId: training.trainingId,
        resourceId: training.trainingResourceId,
        type: 5,
        userId: this.currentUserId,
        date: moment.utc().toDate(),
      },
      registrationOptions: options,
      showTrainingRegistrationModal: true,
    });
  };

  onOpenEmployeeTrainingDetailsModal = training => {
    this.setState({
      selectedEmployeeTraining: training,
      showEmployeeTrainingModal: true,
    });
  };

  render() {
    const { intl, stateService } = this.props;
    const {
      items,
      allSelected,
      initialSearchParams,
      showTrainingRegistrationModal,
      showEmployeeTrainingModal,
      registrationOptions,
      selectedEmployeeTraining,
    } = this.state;

    return (
      <div className="training-registration-overview">
        <SimplePanel article="22061018338076" title={intl.formatMessage(messages.Trainings)}>
          <div className="info-block container-fluid">
            <TrainingOverviewFilters
              filters={initialSearchParams.trainingRegistrationFilters}
              onFilterChange={this.filtersChanged}
              intl={intl}
            />
          </div>
        </SimplePanel>
        <ScrollableTable
          tableClassName="table-grid margin50B"
          loadItems={searchParams => this.loadItems(searchParams)}
          initialSearchParams={initialSearchParams}
          items={items}
          renderRow={item => (
            <TrainingEmployeeOverviewRow
              key={item.id}
              training={item}
              intl={intl}
              handleSelect={this.handleSelect}
              onAddRegistrationClick={this.openTrainingRegistrationModal}
              onOpenEmployeeTrainingDetailsClick={this.onOpenEmployeeTrainingDetailsModal}
              stateService={stateService}
            />
          )}
          noDataLabel={intl.formatMessage(messages.NoTrainingsFound)}
        >
          <ColumnHeader
            className="checkbox-column"
            title={
              <Checkbox extraClass="margin2L" onClick={this.handleSelectAll} value={allSelected} />
            }
          />
          <ColumnHeader title={intl.formatMessage(messages.Name)} />
          <ColumnHeader title={intl.formatMessage(messages.Status)} />
          <ColumnHeader title={intl.formatMessage(messages.Type)} />
          <ColumnHeader title={intl.formatMessage(messages.Mandatory)} />
          <ColumnHeader title={intl.formatMessage(messages.ExpectedTrainingHours)} />
          <ColumnHeader title={intl.formatMessage(messages.RegisteredTrainingHours)} />
          <ColumnHeader title={intl.formatMessage(messages.StartDate)} />
          <ColumnHeader title={intl.formatMessage(messages.EndDate)} />
          <ColumnHeader title={intl.formatMessage(messages.CompletedDate)} />
          <ColumnHeader title={intl.formatMessage(messages.Actions)} className="text-right" />
        </ScrollableTable>
        {showTrainingRegistrationModal && (
          <RegistrationModal
            show
            registration={selectedEmployeeTraining}
            registrationOptions={registrationOptions}
            isEdit={!!selectedEmployeeTraining}
            onSave={this.onTrainingRegistrationSaved}
            onClose={() =>
              this.setState({
                showTrainingRegistrationModal: false,
                selectedEmployeeTraining: null,
              })
            }
          />
        )}
        {showEmployeeTrainingModal && (
          <EmployeeTrainingModal
            show
            intl={this.props.intl}
            training={selectedEmployeeTraining}
            onClose={() =>
              this.setState({
                showEmployeeTrainingModal: false,
                selectedEmployeeTraining: null,
              })
            }
          />
        )}
      </div>
    );
  }
}

TrainingEmployeeOverviewContainer.propTypes = {
  intl: intlShape.isRequired,
  stateService: PropTypes.instanceOf(StateService).isRequired,
  searchParams: PropTypes.shape({}),
};

TrainingEmployeeOverviewContainer.defaultProps = {
  searchParams: {},
};

export default injectStateService(injectIntl(TrainingEmployeeOverviewContainer));
